import { useState, useEffect } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import AdminModal from '../../components/admin/AdminModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import FormField from '../../components/admin/FormField';
import Badge from '../../components/admin/Badge';

function AdminUsers({ apiFetch, toast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/users');
      if (res.ok) setUsers(await res.json());
      else toast.error('Error', 'Failed to load admin users');
    } catch { toast.error('Error', 'Failed to load admin users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const isNew = !editing.id;
      if (isNew) {
        const res = await apiFetch('/api/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: editing.email, password: editing.password, full_name: editing.full_name, role: editing.role }),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
        toast.success('Created', 'Admin user created');
      } else {
        const body = { email: editing.email, full_name: editing.full_name, role: editing.role, status: editing.status };
        if (editing.password) body.password = editing.password;
        const res = await apiFetch(`/api/admin/users/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
        toast.success('Updated', 'Admin user updated');
      }
      setEditing(null);
      await loadUsers();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await apiFetch(`/api/admin/users/${deleteConfirm}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Deleted', 'Admin user removed');
      await loadUsers();
    } catch (err) { toast.error('Error', err.message); }
    finally { setDeleteConfirm(null); }
  };

  const columns = [
    { key: 'email', header: 'Email' },
    { key: 'full_name', header: 'Name' },
    { key: 'role', header: 'Role', render: row => (
      <Badge variant={row.role === 'super_admin' ? 'active' : row.role === 'manager' ? 'info' : 'pending'}>{row.role.replace('_', ' ')}</Badge>
    )},
    { key: 'status', header: 'Status', render: row => <Badge variant={row.status === 'active' ? 'active' : 'suspended'}>{row.status}</Badge> },
    { key: 'last_login', header: 'Last Login', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => row.last_login ? new Date(row.last_login).toLocaleString() : 'Never' },
    { key: 'created_at', header: 'Created', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => new Date(row.created_at).toLocaleDateString() },
    { key: 'actions', header: 'Actions', render: row => (
      <div className="admin-btn-group">
        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => setEditing({ ...row, password: '' })}>Edit</button>
        <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setDeleteConfirm(row.id)}>Delete</button>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader
        title="Admin Users"
        description="Manage admin accounts and roles"
        actions={
          <button className="admin-btn admin-btn--primary" onClick={() => setEditing({ email: '', password: '', full_name: '', role: 'support', status: 'active' })}>
            <i className="fa-solid fa-plus" /> Add User
          </button>
        }
      />

      <div className="admin-card">
        <DataTable columns={columns} data={users} loading={loading} emptyMessage="No admin users" emptyIcon="fa-solid fa-user-shield" />
      </div>

      <AdminModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit Admin User' : 'Add Admin User'}
        footer={
          <>
            <button className="admin-btn admin-btn--secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={saving || !editing?.email || !editing?.full_name || (!editing?.id && !editing?.password)}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {editing && (
          <>
            <FormField label="Email" required>
              <input className="admin-input" type="email" value={editing.email} onChange={e => setEditing(p => ({ ...p, email: e.target.value }))} placeholder="admin@example.com" />
            </FormField>
            <FormField label="Full Name" required>
              <input className="admin-input" type="text" value={editing.full_name} onChange={e => setEditing(p => ({ ...p, full_name: e.target.value }))} placeholder="John Doe" />
            </FormField>
            <FormField label={editing.id ? 'New Password (leave blank to keep)' : 'Password'} required={!editing.id} hint="Minimum 8 characters">
              <input className="admin-input" type="password" value={editing.password} onChange={e => setEditing(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" />
            </FormField>
            <FormField label="Role" required>
              <select className="admin-select" value={editing.role} onChange={e => setEditing(p => ({ ...p, role: e.target.value }))}>
                <option value="super_admin">Super Admin</option>
                <option value="manager">Manager</option>
                <option value="support">Support</option>
              </select>
            </FormField>
            {editing.id && (
              <FormField label="Status">
                <select className="admin-select" value={editing.status} onChange={e => setEditing(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </FormField>
            )}
          </>
        )}
      </AdminModal>

      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Admin User"
        message="Are you sure you want to delete this admin user? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminUsers;
